import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, EyeOff, Mail, Lock, ArrowLeft, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { PlateMindLogo } from "@/components/PlateMindLogo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  // Professional login mode (dietitian, admin, partner)
  const isProfessionalLogin = searchParams.get('type') === 'professional';

  // Handle Google login error from callback
  useEffect(() => {
    const error = searchParams.get('error');
    if (error === 'google_auth_failed') {
      toast.error(t('auth.google_login_failed') || 'Google login failed. Please try again.');
    }
  }, [searchParams, t]);

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    try {
      const response = await fetch(`${API}/auth/google/login`);
      const data = await response.json();
      
      if (data.auth_url) {
        // Redirect to Google OAuth
        window.location.href = data.auth_url;
      } else {
        toast.error(t('auth.google_login_failed') || 'Google login failed');
        setGoogleLoading(false);
      }
    } catch (error) {
      console.error("Google login error:", error);
      toast.error(t('auth.google_login_failed') || 'Google login failed');
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        await login(data.user, data.token);
        toast.success(t('auth.welcome_back') + "!");
        
        // Redirect based on user role
        const userRole = data.user.role;
        if (!data.user.onboarding_completed && userRole === "patient") {
          navigate("/onboarding");
        } else if (userRole === "admin") {
          navigate("/admin");
        } else if (userRole === "dietitian") {
          navigate("/physician");
        } else if (userRole === "partner_admin") {
          navigate("/partner");
        } else if (userRole === "corporate_admin") {
          navigate("/corporate");
        } else {
          navigate("/dashboard");
        }
      } else {
        toast.error(data.detail || t('auth.invalid_credentials'));
      }
    } catch (error) {
      console.error("Login error:", error);
      // Show more detailed error message
      if (error.message === "Failed to fetch") {
        toast.error("Cannot connect to server. Please check your internet connection.");
      } else {
        toast.error(t('auth.login_failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="p-4 md:p-6">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-secondary transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span>{t('common.back')}</span>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-md rounded-3xl border border-orange-100/50 shadow-soft-lift">
          <CardHeader className="text-center space-y-2 pb-2">
            {/* Language Selector */}
            <div className="flex justify-end">
              <LanguageSwitcher variant="compact" />
            </div>
            <div className="mx-auto mb-2">
              <PlateMindLogo size={56} />
            </div>
            <CardTitle className="font-heading text-2xl font-bold text-secondary">{t('auth.welcome_back')}</CardTitle>
            <CardDescription className="text-muted-foreground">
              {t('auth.welcome_back_subtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-4">
            {/* Professional login notice */}
            {isProfessionalLogin && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                <div className="flex items-center justify-center gap-2 text-blue-700">
                  <Briefcase className="w-4 h-4" />
                  <span className="text-sm font-medium">{t('auth.professional_login')}</span>
                </div>
                <p className="text-xs text-blue-600 mt-1">{t('auth.professional_login_subtitle')}</p>
              </div>
            )}

            {/* Email Login Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-secondary font-medium">{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    data-testid="email-input"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 rounded-xl border-2 border-orange-50 focus:border-primary py-5"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-secondary font-medium">{t('auth.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    data-testid="password-input"
                    placeholder={t('auth.enter_password')}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 pr-10 rounded-xl border-2 border-orange-50 focus:border-primary py-5"
                    required
                  />
                  <button
                    type="button"
                    data-testid="toggle-password-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-secondary transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-btn"
                disabled={loading}
                className="w-full rounded-full bg-primary text-white hover:bg-primary/90 py-6 font-bold shadow-button-primary hover:shadow-button-primary-hover transition-all"
              >
                {loading ? t('common.loading') : t('auth.log_in')}
              </Button>
            </form>

            {/* Divider */}
            {!isProfessionalLogin && (
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-orange-100" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-muted-foreground">{t('auth.or_continue_with') || 'Or continue with'}</span>
                </div>
              </div>
            )}

            {/* Google Sign In Button */}
            {!isProfessionalLogin && (
              <Button
                type="button"
                variant="outline"
                data-testid="google-login-btn"
                onClick={handleGoogleLogin}
                disabled={googleLoading}
                className="w-full rounded-full py-6 font-medium border-2 border-gray-200 hover:bg-gray-50 transition-all flex items-center justify-center gap-3"
              >
                {googleLoading ? (
                  <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                {googleLoading ? t('common.loading') : (t('auth.continue_with_google') || 'Continue with Google')}
              </Button>
            )}

            <div className="text-center space-y-2">
              <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                {t('auth.forgot_password')}
              </Link>
              
              {/* Hide signup option for professional login */}
              {!isProfessionalLogin && (
                <p className="text-sm text-muted-foreground">
                  {t('auth.no_account')}{" "}
                  <Link to="/signup" className="text-primary font-medium hover:underline" data-testid="signup-link">
                    {t('auth.sign_up')}
                  </Link>
                </p>
              )}
              
              {/* Toggle between user and professional login */}
              {!isProfessionalLogin && (
                <div className="pt-2 border-t border-border mt-4">
                  <Link to="/login?type=professional" className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center justify-center gap-1">
                    <Briefcase className="w-3 h-3" />
                    {t('auth.professional_login_link')}
                  </Link>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Login;
